import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses, SearchBarProps } from '@paintscout/ui'
import { SearchBar, useDialogs, AlertDialog, useClientOptions, useIsMobile, useDebouncedCallback } from '@paintscout/ui'
import LocationSearchFilters from './LocationSearchFilters'
import LocationSearchFilterChips from './LocationSearchFilterChips'
import { isEqual } from 'apollo-utilities'
import { useLocation } from 'react-router-dom'
import type { ValueFilter } from 'json-to-lucene'
import { getUrlQuery, setUrlQuery } from 'shared/util/routing'
import { getFiltersFromLocation } from '../QuoteSearchBar'
import type { UrlQuery } from '../QuoteSearchBar'
import { useApolloClient } from '@apollo/react-hooks'
import { gql } from 'apollo-boost'
import FileSaver from 'file-saver'
import * as Sentry from '@sentry/react'
import { jsonToLucene } from 'json-to-lucene'
import moment from 'moment'

export interface LocationSearchBarProps extends Omit<SearchBarProps, 'onSearch' | 'showFilters' | 'setShowFilters'> {
  classes?: StyleClasses<typeof useStyles>
  loading?: boolean
  filters?: ValueFilter[]
  onSearch?: (query: string, filters: ValueFilter[], isInitialSearch?: boolean) => any
}

const useStyles = makeStyles<Theme, LocationSearchBarProps>(
  (_theme) => ({
    root: {}
  }),
  { name: 'LocationSearchBar' }
)

function LocationSearchBar({ loading, onSearch, ...props }: LocationSearchBarProps) {
  const location = useLocation()
  const classes = useStyles(props)
  const mobile = useIsMobile()
  const apolloClient = useApolloClient()
  const { options } = useClientOptions()
  const [showFilters, setShowFilters] = useState(false)
  const placeholder = `Search location by name, city, or state...`
  const [exporting, setExporting] = useState(false)
  const { openDialog, dismissDialog } = useDialogs()
  const dateFormat = options.options.dateFormat.momentValue

  // the normal text of the search straight from the input
  const [queryText, setQueryText] = useState(getUrlQuery<UrlQuery>({ location }).search)

  // the committed/current filters for the results
  const [filters, setFilters] = useState(props.filters ?? getFiltersFromLocation(location, true))
  console.log({ filters })
  // filters state before the user commits them by hitting search
  const [uncommittedFilters, setUncommittedFilters] = useState(filters)

  // trigger search when props.filters changes
  useEffect(() => {
    if (!isEqual(filters, props.filters)) {
      handleSearch({ filters: props.filters })
    }
    // eslint-disable-next-line
  }, [props.filters])
  // trigger search on mount
  useEffect(() => {
    handleSearch({ filters, query: queryText }, true)
  }, [])

  const handleSearch = (args: { query?: string; filters?: ValueFilter[] } = {}, isInitialSearch?: boolean) => {
    const { query = queryText } = args

    // the filter values (`filters` variable from state is in scope, so we'll call it values)
    const values = [...(args.filters ?? uncommittedFilters)]

    // update filter states
    setFilters(values)
    setUncommittedFilters(values)

    // update the typed-in user text
    setQueryText(query)

    // remove filters with null or undefined values
    const sanitizedValues = values.filter((filter) => {
      // != operator rather than !== is intentional - it checks for null or undefined - update to catch empty arrays
      return (
        filter.value != null &&
        (!Array.isArray(filter.value) || (Array.isArray(filter.value) && filter.value.filter(Boolean).length))
      )
    })

    setUrlQuery({
      location,
      params: { search: query || null, filters: sanitizedValues },
      replaceParams: true
    })

    onSearch(query, sanitizedValues, isInitialSearch)
  }

  const debouncedSearch = useDebouncedCallback(handleSearch, 100)

  const handleFilterClear = () => {
    setUncommittedFilters([])
  }

  function deleteFilter({ id, valueIndex: _valueIndex }: { id: string; valueIndex?: number }) {
    // Remove the only filter of type
    handleSearch({
      filters: filters.filter((filter) => {
        return filter.id !== id
      })
    })
  }

  const handleExport = async () => {
    const dateFilter = uncommittedFilters.find((filter) => filter.id === 'quote_date')
    // "Quote 01/01/2000 - 02/01/2000"
    const fileName = `Locations ${moment(dateFilter.value[0]).format(dateFormat)} - ${moment(
      dateFilter.value[1]
    ).format(dateFormat)}.csv`

    setExporting(true)
    try {
      const luceneQuery = jsonToLucene({
        query: queryText,
        filters: uncommittedFilters
      })

      const sanitizedQuery = luceneQuery ? luceneQuery : '*:*'

      const { data } = await apolloClient.query({
        query: gql`
          query ($query: String!, $sort: [String!]) {
            exportClients(query: $query, sort: $sort) {
              csv
              rows
            }
          }
        `,
        variables: {
          query: sanitizedQuery,
          // Need 2 sort params for tiebreaker, keep sort from quoteList if present else same default
          sort: [getUrlQuery<UrlQuery>({ location })?.sort ?? '-updated']
        }
      })

      if (data.exportClients.rows) {
        FileSaver.saveAs(new Blob([data.exportQuotes.csv]), fileName)
      } else {
        openDialog(AlertDialog, {
          message: 'No rows were found to export for this search query.',
          onConfirm: () => {
            dismissDialog()
          }
        })
      }
    } catch (error) {
      Sentry.captureException(error)
    }
    setExporting(false)
  }

  return (
    <div>
      <SearchBar
        inputProps={{
          'aria-label': `Search locations`,
          // recommended aria for loading indicator (also used in e2e tests)
          'aria-busy': loading,
          'aria-live': loading ? 'polite' : undefined
        }}
        filterMenuCollapse={!mobile}
        defaultValue={queryText}
        placeholder={placeholder}
        isExporting={exporting}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        filterContent={<LocationSearchFilters filters={uncommittedFilters} onChange={setUncommittedFilters} />}
        onClear={handleFilterClear}
        onExport={handleExport}
        onChange={(q) => {
          const cleanQuery = q.replace(/\\/g, '/')
          debouncedSearch({ query: cleanQuery })
        }}
        onSearch={(q) => {
          handleSearch({ query: q, filters: uncommittedFilters })
        }}
        {...props}
      />
      <div className={classes.filterChips}>
        <LocationSearchFilterChips
          show={filters.length > 0}
          filters={filters}
          onDelete={(filter, valueIndex) => deleteFilter({ id: filter.id, valueIndex })}
        />
      </div>
    </div>
  )
}

export default LocationSearchBar
